import moment from 'moment/min/moment-with-locales.js';
import { isMoment, tz } from 'moment-timezone';
import momentLib from 'moment';
import { usePage } from '@inertiajs/vue3';

const page = usePage();

const getDefaultTimezone = (timezone = null) => {
  if (timezone) return timezone;
  return page?.props?.master_config?.time_zone || page?.props?.timezone || null;
};

const formatDate = (date) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  if (moment(date).isValid()) return moment(date).locale(locale).format('ll');
};

const formatDateShort = (date) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  if (moment(date).isValid()) return moment(date).locale(locale).format('L');
};

const formatWeekDay = (date) => {
  let weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return weekdays[moment(date).weekday()];
};

const formatDateShortFromTimestamp = (date) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  if (moment(date * 100).isValid()) {
    return moment.unix(date).locale(locale).format('L');
  }
};

const isDateValid = (date) => {
  return moment(date).isValid();
};

const isValidDate = (value) => {
  if (value === null || value === undefined || value === '') return false;

  if (typeof value === 'string') {
    const match = value.match(/^(-?\d{4,})-/);
    if (match) {
      const year = Number(match[1]);
      if (!Number.isFinite(year) || year <= 0) return false;
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(parsed.getTime());
};

const formatDateShortFromTimestampNoTimezone = (date) => {
  if (moment(date * 100).isValid()) {
    return momentLib.unix(date).utc().format('MM/DD/YYYY');
  }
};

const formatWeekFromTimestamp = (date) => {
  return moment.unix(date).week();
};

const todayDate = (timezone = null) => {
  const resolvedTimezone = getDefaultTimezone(timezone);
  return formatDatabaseDate(resolvedTimezone ? tz(resolvedTimezone) : moment());
};

const displayDays = () => {
  let days = {
    0: '-- DAY -- ',
  };
  for (let i = 0; i <= 31; i++) {
    days[i] = i + ' Days';
  }
  return days;
};

const displayHours = () => {
  let hours = {};

  for (let i = 0; i <= 12; i++) {
    hours[i] = i + ' HR';
  }

  return hours;
};

const displayMinutes = () => {
  let minutes = {};
  for (let i = 0; i < 12; i++) {
    minutes[i * 5] = i * 5 + ' Min';
  }
  return minutes;
};

const currentTimestampLong = () => {
  return moment().format('LLLL');
};

const weekAgo = () => {
  return formatDatabaseDate(moment().subtract(6, 'days'));
};

const localDate = (date, timezone) => {
  let local = tz(date, 'America/Chicago');
  return local.clone().tz(timezone);
};

const flatPickerDateFormat = (date) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  if (date == null || date == '') {
    return;
  }
  if (isMoment(date)) {
    return date.toDate;
  }

  //check for Month View
  let monthView = moment(date, 'MM/YYYY');
  if (monthView.isValid()) {
    return monthView.format('MM/YYYY');
  }

  return moment(date).locale(locale).format('L');
};

const flatPickerDateTimeFormat = (datetime) => {
  if (datetime == null) {
    return;
  }

  if (isMoment(datetime)) {
    return datetime.toDate();
  }

  if (datetime instanceof Date) {
    return datetime;
  }

  const parsed = moment(datetime, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD hh:mm A'], true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const formatDatabaseDate = (date) => {
  return moment(date).format('YYYY-MM-DD');
};

const formatDatabaseTime = (dateTime) => {
  return moment(dateTime).format('HH:mm');
};

const formatDateTimeFromDatabase = (date, time) => {
  let parsedTime = moment(time, 'HH:mm');
  return moment(date, 'YYYY-MM-DDZ hh:mm:ss')
    .set({
      hour: parsedTime.get('hour'),
      minute: parsedTime.get('minute'),
    })
    .format('MMM Do YYYY hh:mm A');
};

const formatDateTime = (date, timezone = null) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  let targetTimezone = getDefaultTimezone(timezone);
  let formatted = targetTimezone
    ? tz(date, targetTimezone).locale(locale)
    : moment(date).locale(locale);
  if (formatted.isValid()) {
    return formatted.format('lll');
  }
};

const formatDateTimeLocal = (date, timezone) => {
  let resolvedTimezone = getDefaultTimezone(timezone);
  let formatted = resolvedTimezone
    ? tz(date, 'America/Chicago').tz(resolvedTimezone)
    : moment(date);

  if (formatted.isValid()) {
    return formatted.format('lll');
  }

  return '';
};

const parseDateWithTimezone = (date, timezone) => {
  let resolvedTimezone = getDefaultTimezone(timezone);
  return resolvedTimezone ? tz(date, resolvedTimezone) : moment(date);
};

const formatDateTimeWithFormat = (date, timezone, format = 'MM/DD/YYYY hh:mm A') => {
  const parsed = parseDateWithTimezone(date, timezone);
  if (parsed.isValid()) {
    return parsed.format(format);
  }
  return '';
};

const formatTimeStamp = (timestamp) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  let targetTimezone = getDefaultTimezone();
  let formatted = targetTimezone
    ? moment.unix(timestamp).tz(targetTimezone).locale(locale)
    : moment.unix(timestamp).locale(locale);
  return formatted.format('lll');
};

const nowForShop = (masterConfig) => {
  return tz(masterConfig.time_zone);
};

const humanTimeFromNow = (date, timezone) => {
  return tz(date, timezone).fromNow();
};

const isSameWeek = (date, compareDate) => {
  return date.isSame(compareDate, 'week') && date.isSame(compareDate, 'year');
};

const isSameMonth = (date, compareDate) => {
  return date.isSame(compareDate, 'month') && date.isSame(compareDate, 'year');
};

const timeDiff = (time, time_zone) => {
  var CT = tz(time_zone).format('YYYY-MM-DD HH:mm:ss');
  var duration = moment.duration(moment(CT).diff(time));

  let diff = {
    days: parseInt(duration.asDays()),
    hours: parseInt(duration.asHours()),
    mins: parseInt(duration.asMinutes()),
    seconds: parseInt(duration.asSeconds()),
    label: '',
  };

  if (diff['days'] >= 1)
    diff.label = diff['days'] == 1 ? `${diff.days} Day ago` : `${diff.days} Days ago`;
  else if (diff['hours'] >= 1)
    diff.label = diff['hours'] == 1 ? `${diff.hours} Hr ago` : `${diff.hours} Hr ago`;
  else if (diff['mins'] >= 1)
    diff.label = diff['mins'] == 1 ? `${diff.mins} Min ago` : `${diff.mins} Mins ago`;
  else if (diff.seconds < 60) diff.label = 'just now';
  return diff;
};

const formatMonthYear = (date = new Date()) => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  return moment(date).locale(locale).format('MM/YYYY');
};

const daysOFWeek = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export {
  formatDate,
  formatDateTime,
  formatTimeStamp,
  formatDatabaseDate,
  humanTimeFromNow,
  todayDate,
  weekAgo,
  daysOFWeek,
  flatPickerDateFormat,
  flatPickerDateTimeFormat,
  formatDatabaseTime,
  formatDateTimeFromDatabase,
  localDate,
  formatDateShort,
  nowForShop,
  displayDays,
  displayHours,
  displayMinutes,
  formatDateShortFromTimestamp,
  formatWeekFromTimestamp,
  formatDateShortFromTimestampNoTimezone,
  isSameWeek,
  isSameMonth,
  isDateValid,
  timeDiff,
  formatMonthYear,
  formatWeekDay,
  currentTimestampLong,
  formatDateTimeLocal,
  parseDateWithTimezone,
  formatDateTimeWithFormat,
  isValidDate,
};
